/**
 * CandidateComparison — Phase 4e side-by-side comparison (2–4 candidates for
 * a role).
 *
 * COMPLIANCE / wedge discipline: this is a pure LAYOUT of facts that already
 * exist — human scorecard ratings/recommendations, summary topics/quotes, and
 * (if already generated) mindmap topic-coverage counts. It renders NO ranking,
 * NO composite score, NO "best fit" verdict. Columns follow the user's
 * selection order. The humans compare; the tool lays out the record.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, ShieldCheck, Quote, Network, Info } from "lucide-react";
import AppShell from "../components/Layout/AppShell";
import api from "../lib/api";
import { RecommendationPill } from "../components/Scorecards/Scorecards";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip";

const STROKE = 1.5;

function LabelCell({ children, testid }) {
  return (
    <div
      className="sticky left-0 z-10 border-b border-r border-ink-100 bg-ink-50/80 px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-ink-500 backdrop-blur"
      data-testid={testid}
    >
      {children}
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div
      className="sticky left-0 z-10 border-b border-ink-200 bg-white px-4 pb-1.5 pt-5 font-display text-[13px] font-medium text-ink-900"
      style={{ gridColumn: "1 / -1" }}
    >
      {children}
    </div>
  );
}

export default function CandidateComparison() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const candidateIds = params.get("candidate_ids") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true);
    api
      .get(`/roles/${roleId}/compare`, { params: { candidate_ids: candidateIds } })
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load the comparison."))
      .finally(() => setLoading(false));
  }, [roleId, candidateIds]);

  const cols = data?.candidates || [];
  const gridStyle = useMemo(
    () => ({ gridTemplateColumns: `180px repeat(${cols.length || 1}, minmax(240px, 1fr))` }),
    [cols.length],
  );

  if (loading) {
    return <AppShell><div className="flex justify-center p-12"><Loader2 className="h-5 w-5 animate-spin text-ink-400" strokeWidth={STROKE} /></div></AppShell>;
  }
  if (err || !data) {
    return (
      <AppShell>
        <button onClick={() => navigate(`/roles/${roleId}`)} className="inline-flex items-center gap-1 text-[13px] text-ink-500 hover:text-ink-900">
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={STROKE} /> Back to role
        </button>
        <div className="hp-card mt-4 p-6 text-[14px] text-red-700" data-testid="comparison-error">{err}</div>
      </AppShell>
    );
  }

  const { role, competency_names = [], mindmap_categories = [] } = data;

  return (
    <AppShell>
      <div data-testid="comparison-page" className="space-y-5">
        <header>
          <button onClick={() => navigate(`/roles/${roleId}`)} className="inline-flex items-center gap-1 text-[13px] text-ink-500 hover:text-ink-900" data-testid="comparison-back">
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={STROKE} /> Back to role
          </button>
          <h1 className="font-display mt-3 flex items-center gap-2 text-3xl font-medium tracking-tight text-ink-900">
            <span data-testid="comparison-title">Comparing {cols.length} candidates</span>
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-ink-400 transition hover:text-ink-700"
                    data-testid="comparison-info-tooltip"
                    aria-label="What this view is and isn't"
                  >
                    <Info className="h-4 w-4" strokeWidth={STROKE} />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-ink-900 text-left text-[12px] leading-relaxed text-white">
                  <p className="font-medium">What this is</p>
                  <p className="mt-0.5 text-ink-200">A side-by-side layout of facts already on record — the scorecard ratings &amp; recommendations your team entered, topics discussed, and verbatim quotes.</p>
                  <p className="mt-2 font-medium">What this isn&rsquo;t</p>
                  <p className="mt-0.5 text-ink-200">It does not rank candidates, compute a score, or recommend a hire. No AI judgment is applied — the decision is yours.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </h1>
          <p className="mt-1 text-[13px] text-ink-600">{role.title}{role.department ? ` · ${role.department}` : ""}</p>
        </header>

        {/* Compliance banner */}
        <div className="flex items-start gap-2 rounded-md border border-emerald-800/15 bg-emerald-50 px-4 py-2.5 text-[12.5px] text-emerald-900" data-testid="comparison-compliance-banner">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={STROKE} />
          <span>
            This view lays out what each candidate said and the scorecard ratings your team entered. It does <strong>not</strong> rank candidates or recommend a hire — that decision is yours.
          </span>
        </div>

        {/* Grid */}
        <div className="hp-card overflow-x-auto p-0">
          <div className="grid min-w-max text-[13px]" style={gridStyle} data-testid="comparison-grid">
            {/* Column headers */}
            <div className="sticky left-0 z-20 border-b border-r border-ink-200 bg-white px-4 py-3" />
            {cols.map((c) => (
              <div key={c.candidate.candidate_id} className="border-b border-ink-200 bg-white px-4 py-3" data-testid={`comparison-col-${c.candidate.candidate_id}`}>
                <Link to={`/candidates/${c.candidate.candidate_id}`} className="font-display text-[15px] font-medium text-ink-900 hover:text-emerald-800">
                  {c.candidate.name}
                </Link>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-ink-500">
                  {c.candidate.current_status && (
                    <span className="inline-flex rounded border border-ink-200 bg-ink-50 px-1.5 py-0.5 capitalize text-ink-600">{c.candidate.current_status}</span>
                  )}
                  <span>{c.rounds_for_role} round{c.rounds_for_role === 1 ? "" : "s"}</span>
                  {c.last_interview_date && <span>· last {new Date(c.last_interview_date).toLocaleDateString()}</span>}
                </div>
              </div>
            ))}

            {/* Recommendations — INDIVIDUAL pills only (one per submitted
                scorecard). Deliberately NO aggregated tally: a vote count reads
                as a soft ranking, which this view must never imply. */}
            <SectionHeader>Recommendations (entered by your team)</SectionHeader>
            <LabelCell testid="comparison-row-recommendations">Recommendation</LabelCell>
            {cols.map((c) => {
              const allCards = c.scorecards || [];
              return (
                <div key={c.candidate.candidate_id} className="border-b border-r border-ink-100 px-4 py-3" data-testid={`comparison-rec-${c.candidate.candidate_id}`}>
                  {allCards.length === 0 ? (
                    <span className="text-[12px] text-ink-400">No submitted scorecards</span>
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      {allCards.map((sc) => (
                        <div key={sc.scorecard_id} className="flex items-center gap-1.5">
                          <RecommendationPill recommendation={sc.recommendation} status="submitted" fillerName={sc.filled_by_name} />
                          <span className="text-[11px] text-ink-500">{sc.filled_by_name} · R{sc.round_number}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Scorecard ratings per competency */}
            {competency_names.length > 0 && (
              <>
                <SectionHeader>Scorecard ratings (1–5, as entered)</SectionHeader>
                {competency_names.map((comp) => (
                  <RatingRow key={comp} comp={comp} cols={cols} />
                ))}
              </>
            )}

            {/* Key topics */}
            <SectionHeader>Key topics discussed</SectionHeader>
            <LabelCell testid="comparison-row-topics">Topics</LabelCell>
            {cols.map((c) => (
              <div key={c.candidate.candidate_id} className="border-b border-r border-ink-100 px-4 py-3" data-testid={`comparison-topics-${c.candidate.candidate_id}`}>
                {(c.key_topics || []).length === 0 ? (
                  <span className="text-[12px] text-ink-400">—</span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {c.key_topics.map((t, i) => (
                      <span key={i} className="inline-flex rounded-md border border-ink-200 bg-ink-50 px-2 py-0.5 text-[11.5px] text-ink-700">{t}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Topic coverage from mindmap */}
            {mindmap_categories.length > 0 && (
              <>
                <SectionHeader>
                  <span className="inline-flex items-center gap-1.5"><Network className="h-3.5 w-3.5 text-ink-400" strokeWidth={STROKE} /> Topic-map coverage (topics per category)</span>
                </SectionHeader>
                {mindmap_categories.map((cat) => (
                  <RowGeneric key={cat.key} label={cat.label} testid={`comparison-row-coverage-${cat.key}`}>
                    {cols.map((c) => {
                      const counts = c.mindmap_topic_counts;
                      return (
                        <div key={c.candidate.candidate_id} className="border-b border-r border-ink-100 px-4 py-2.5 text-[13px] text-ink-800">
                          {counts ? <span className="font-medium">{counts[cat.key] ?? 0}</span> : <span className="text-ink-400">no map</span>}
                        </div>
                      );
                    })}
                  </RowGeneric>
                ))}
              </>
            )}

            {/* Notable quotes */}
            <SectionHeader>Notable quotes (verbatim)</SectionHeader>
            <LabelCell testid="comparison-row-quotes"><Quote className="mb-1 h-3.5 w-3.5 text-ink-400" strokeWidth={STROKE} /> Quotes</LabelCell>
            {cols.map((c) => (
              <div key={c.candidate.candidate_id} className="border-b border-r border-ink-100 px-4 py-3" data-testid={`comparison-quotes-${c.candidate.candidate_id}`}>
                {(c.notable_quotes || []).length === 0 ? (
                  <span className="text-[12px] text-ink-400">—</span>
                ) : (
                  <div className="space-y-2.5">
                    {c.notable_quotes.map((q, i) => (
                      <div key={i} className="rounded-md border border-ink-100 bg-ink-50/60 p-2">
                        <div className="mb-0.5 text-[10.5px] font-medium text-emerald-800">R{q.round_number}{q.timestamp ? ` · ~${q.timestamp}` : ""}</div>
                        <p className="text-[12px] italic leading-relaxed text-ink-700">“{q.quote}”</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function RowGeneric({ label, testid, children }) {
  return (
    <>
      <LabelCell testid={testid}>{label}</LabelCell>
      {children}
    </>
  );
}

function RatingRow({ comp, cols }) {
  return (
    <>
      <LabelCell testid={`comparison-row-rating-${comp.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{comp}</LabelCell>
      {cols.map((c) => {
        const entries = (c.scorecards || [])
          .map((sc) => ({ filler: sc.filled_by_name, r: sc.ratings?.[comp]?.rating, notes: sc.ratings?.[comp]?.notes }))
          .filter((e) => e.r != null);
        return (
          <div key={c.candidate.candidate_id} className="border-b border-r border-ink-100 px-4 py-2.5">
            {entries.length === 0 ? (
              <span className="text-[12px] text-ink-400">—</span>
            ) : (
              <div className="flex flex-wrap gap-2">
                {entries.map((e, i) => (
                  <span key={i} className="inline-flex items-baseline gap-1 rounded-md border border-emerald-800/20 bg-emerald-50 px-2 py-0.5" title={e.notes || ""}>
                    <span className="text-[13px] font-semibold text-emerald-900">{e.r}</span>
                    <span className="text-[10px] text-emerald-700/70">/5</span>
                    <span className="ml-1 text-[10px] text-ink-500">{e.filler}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
